import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTeamCalendarRange } from '../../api/reportsApi';
import { usePermissions } from '../PermissionsContext';
import ConsultantTimesheetPanel from './ConsultantTimesheetPanel';
import {
  DOW_ABBR, combinationClass, fmtDdMm, fmtHours, fmtIso, isWeekend,
  navStep, parseIso, periodLabel, rangeOf,
} from './calendarUtils';
import './TeamCalendarOverviewPage.css';

// TC-S1 + TC-S2 — Team Calendar Overview: month / week / day views over
// the team-calendar/range endpoint, with a docked ConsultantTimesheetPanel
// that opens on any cell click. The old DayDetailModal was retired in
// TC-S2; the panel supersedes it.

function dayTooltip(consultantName, day) {
  const b = fmtHours(day.billableHours);
  const n = fmtHours(day.nonBillableHours);
  const l = fmtHours(day.leaveHours);
  let tail = '';
  if (day.type === 'PaidLeave') tail = ' (Paid leave)';
  else if (day.type === 'SickLeave') tail = ' (Sick leave)';
  return `${consultantName} · ${fmtDdMm(parseIso(day.date))}.${String(parseIso(day.date).getFullYear()).slice(2)} — billable ${b}, non-billable ${n}, leave ${l}${tail}`;
}

// TC-S4 — per-type helpers for the month toggle. Each mode surfaces
// exactly one component; `sl` reads the additive ShortLeaveHours so a
// full-day PaidLeave/SickLeave does NOT contribute to "Short leaves".
const HOURS_FILTER_META = {
  all: { label: 'All', tag: null, cellClass: null },
  b: { label: 'Billable', tag: 'billableHours', cellClass: 'tc-b' },
  nb: { label: 'Non-billable', tag: 'nonBillableHours', cellClass: 'tc-nb' },
  sl: { label: 'Short leaves', tag: 'shortLeaveHours', cellClass: 'tc-lv' },
};

const perTypeCellValue = (day, filter) => {
  const meta = HOURS_FILTER_META[filter];
  if (!meta || !meta.tag) return 0;
  return Number(day?.[meta.tag]) || 0;
};

const perTypeTooltip = (consultantName, day, filter) => {
  const meta = HOURS_FILTER_META[filter];
  const label = meta?.label ?? 'All';
  const val = fmtHours(perTypeCellValue(day, filter));
  const dt = parseIso(day.date);
  return `${consultantName} · ${fmtDdMm(dt)}.${String(dt.getFullYear()).slice(2)} — ${label.toLowerCase()} ${val}`;
};

function ConsultantFunnel({ allNames, selected, onChange, onClose, active }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q) return allNames;
    const lq = q.toLowerCase();
    return allNames.filter((v) => v.toLowerCase().includes(lq));
  }, [allNames, q]);
  const toggle = (name) => {
    const s = new Set(selected);
    if (s.has(name)) s.delete(name); else s.add(name);
    onChange(Array.from(s));
  };
  return (
    <div className="tc-fpanel" data-testid="tc-cons-filter-panel" onClick={(e) => e.stopPropagation()}>
      <input
        type="text" placeholder="search…" value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="tc-cons-filter-search"
      />
      <div className="tc-vals">
        {filtered.length === 0 && <div style={{ color: '#999', fontSize: 11, padding: 4 }}>No consultants match.</div>}
        {filtered.map((name) => (
          <label key={name} title={name}>
            <input
              type="checkbox" checked={selected.includes(name)}
              onChange={() => toggle(name)}
              data-testid={`tc-cons-filter-val-${name}`}
            /> {name}
          </label>
        ))}
      </div>
      <span
        className="tc-clr" data-testid="tc-cons-filter-clear"
        onClick={() => { onChange([]); onClose(); }}
      >Clear filter</span>
      {active && <span style={{ marginLeft: 10, fontSize: 11, color: '#888' }}>{selected.length} selected</span>}
    </div>
  );
}

export default function TeamCalendarOverviewPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const allowed = hasPermission?.('Timesheet.ViewProjectReports');

  const [view, setView] = useState('m');
  const [anchor, setAnchor] = useState(() => new Date());
  const [consFilter, setConsFilter] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  // TC-S4 — month-only hours-type toggle ('all' | 'b' | 'nb' | 'sl').
  // Client-side switch — the range payload already carries every
  // component, so no re-fetch on change. Ignored in week/day.
  const [hoursFilter, setHoursFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // TC-S3.1 — stale-while-revalidate. When the user clicks ◀/▶ we
  // keep the current table visible and mark it .tc-refreshing (a
  // dim overlay) instead of flashing the whole Loading placeholder.
  // The full placeholder still shows on first load and on view
  // switch (where a different table is genuinely coming).
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // TC-S2 — docked consultant panel state. { employeeId, name, date }
  // when the map's cell click surfaces a consultant; null = closed.
  const [consPanel, setConsPanel] = useState(null);

  const filterAnchorRef = useRef(null);
  const scrollHostRef = useRef(null);

  const range = useMemo(() => rangeOf(view, anchor), [view, anchor]);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const handler = (ev) => {
      if (filterAnchorRef.current && filterAnchorRef.current.contains(ev.target)) return;
      setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  const load = useCallback(async () => {
    // TC-S3.1 — stale-while-revalidate: only show the full "Loading…"
    // placeholder on the very first load (data == null) or right
    // after a view switch (the switch handler zeroes data). For a
    // period change the table stays in place, only dimmed via
    // .tc-refreshing.
    setData((prev) => {
      if (prev == null) setLoading(true); else setRefreshing(true);
      return prev;
    });
    setError('');
    try {
      const dto = await getTeamCalendarRange(fmtIso(range.from), fmtIso(range.to), null);
      setData(dto);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load team calendar.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  // TC-S2 — the panel opens with just ViewProjectReports; notes are
  // redacted server-side when the caller lacks ViewOthersNotes.
  const openPanel = useCallback((employeeId, name, isoDate) => {
    setConsPanel({ employeeId, name, date: parseIso(isoDate) });
  }, []);

  // Whenever the panel's employee or date changes, scroll the marked
  // team-cell into view so operators never lose sight of context.
  useEffect(() => {
    if (!consPanel) return;
    const iso = fmtIso(consPanel.date);
    const testids = [
      `tc-cell-${consPanel.employeeId}-${iso}`,
      `tc-cell-sum-${consPanel.employeeId}-${iso}`,
      `tc-cell-sum-${consPanel.employeeId}`,
    ];
    for (const tid of testids) {
      const el = document.querySelector(`[data-testid="${tid}"]`);
      // jsdom lacks scrollIntoView — guard so unit tests can still
      // exercise the marker path without spinning up a real DOM.
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        break;
      }
    }
  }, [consPanel]);

  if (!permLoading && !allowed) {
    return (
      <div className="reports-page tc-page">
        <p className="empty-note">You don't have access to project reports.</p>
      </div>
    );
  }

  const consultants = data?.consultants || [];
  const dayTotals = data?.dayTotals || [];
  const filteredConsultants = consFilter.length === 0
    ? consultants
    : consultants.filter((c) => consFilter.includes(c.name));
  const allNames = consultants.map((c) => c.name);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = (isoDate) => parseIso(isoDate) > today;

  // Reusable per-column cell for week + day views. `columnClass` is
  // the intrinsic paint of the column (b/nb/lv); the whole row is
  // clickable in this slice (TC-S2 gates the panel on ViewProjectReports).
  const ColCell = ({ value, columnClass, isoDate, employeeId, consultantName, testid, big = false, extraCls = '' }) => {
    const hasVal = value > 0;
    const cls = ['tc-cell', big ? 'tc-big' : '', hasVal ? columnClass : '', extraCls].filter(Boolean).join(' ');
    return (
      <td>
        <span
          className={cls}
          data-testid={testid}
          onClick={() => openPanel(employeeId, consultantName, isoDate)}
        >
          {hasVal ? fmtHours(value) : '·'}
        </span>
      </td>
    );
  };

  const isSel = (employeeId, isoDate) => consPanel
    && consPanel.employeeId === employeeId
    && fmtIso(consPanel.date) === isoDate;

  // ─── Month view ──────────────────────────────────────────────────────
  const renderMonth = () => {
    const days = data?.consultants?.[0]?.days || dayTotals.map((dt) => ({ date: dt.date }));
    const cols = days.map((d) => ({ iso: d.date, dt: parseIso(d.date) }));
    // TC-S4 — team-totals never carry ShortLeaveHours (the range DTO's
    // day-total doesn't expose it). In "Short leaves" mode we compute
    // the team total by summing across consultants' cell values.
    const teamShortLeaveByDate = new Map();
    if (hoursFilter === 'sl') {
      dayTotals.forEach((dt) => {
        let s = 0;
        filteredConsultants.forEach((c) => {
          const cellDay = c.days.find((d) => d.date === dt.date);
          s += Number(cellDay?.shortLeaveHours) || 0;
        });
        teamShortLeaveByDate.set(dt.date, s);
      });
    }
    return (
      <>
        {/* TC-S4 — hours-type toggle, month view only. Client-side —
            data is already in the payload. */}
        <div className="tc-htype" data-testid="tc-htype">
          <span className="tc-htype-lbl">Hours type</span>
          <div className="tc-seg tc-htype-seg">
            {[['all', 'All'], ['b', 'Billable'], ['nb', 'Non-billable'], ['sl', 'Short leaves']].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                className={hoursFilter === k ? 'on' : ''}
                data-testid={`tc-htype-${k}`}
                onClick={() => setHoursFilter(k)}
              >{lbl}</button>
            ))}
          </div>
        </div>
        <table className="tc-g" data-testid="tc-table">
          <colgroup>
            <col style={{ width: 180 }} />
            {cols.map((c) => (
              <col key={c.iso} style={isWeekend(c.dt) ? { width: 30 } : undefined} />
            ))}
            <col style={{ width: 64 }} />
          </colgroup>
          <thead>
            <tr>
              <th className="tc-l" style={{ position: 'relative' }} ref={filterAnchorRef}>
                Consultant
                <span
                  className={`tc-fun${consFilter.length > 0 ? ' act' : ''}`}
                  data-testid="tc-cons-funnel"
                  onClick={(e) => { e.stopPropagation(); setFilterOpen((v) => !v); }}
                >▼</span>
                {filterOpen && (
                  <ConsultantFunnel
                    allNames={allNames}
                    selected={consFilter}
                    onChange={setConsFilter}
                    onClose={() => setFilterOpen(false)}
                    active={consFilter.length > 0}
                  />
                )}
              </th>
              {cols.map((c) => (
                <th key={c.iso} className={isWeekend(c.dt) ? 'tc-we' : ''} data-testid={`tc-th-${c.iso}`}>
                  {c.dt.getDate()}<br />{DOW_ABBR[c.dt.getDay()]}
                </th>
              ))}
              <th>Σ</th>
            </tr>
          </thead>
          <tbody>
            {filteredConsultants.map((c) => {
              const total = hoursFilter === 'all'
                ? c.days.reduce((a, d) => a + (Number(d.billableHours) || 0) + (Number(d.nonBillableHours) || 0) + (Number(d.leaveHours) || 0), 0)
                : c.days.reduce((a, d) => a + perTypeCellValue(d, hoursFilter), 0);
              return (
                <tr key={c.employeeId} data-testid={`tc-row-${c.employeeId}`}>
                  <td className="tc-name" title={c.name}>{c.name}</td>
                  {c.days.map((d) => {
                    const dObj = parseIso(d.date);
                    if (isWeekend(dObj) || d.type === 'Weekend') {
                      return <td key={d.date} className="tc-we" />;
                    }
                    // TC-S2 §3.2 MINOR fix — Holiday cell renders a
                    // dimmed "H" (like the old NF-S3 page) so the map
                    // distinguishes public holidays from weekends.
                    if (d.type === 'Holiday') {
                      return (
                        <td key={d.date} className="tc-we">
                          <span style={{ fontSize: 10, color: '#a08b3a' }} title="Holiday">H</span>
                        </td>
                      );
                    }
                    const future = isFuture(d.date);
                    if (hoursFilter === 'all') {
                      const sum = (Number(d.billableHours) || 0) + (Number(d.nonBillableHours) || 0) + (Number(d.leaveHours) || 0);
                      const gap = !future && sum === 0;
                      const cls = combinationClass(d);
                      const clickable = !future && (sum > 0 || gap);
                      const sel = isSel(c.employeeId, d.date);
                      return (
                        <td key={d.date}>
                          <span
                            className={`tc-cell ${cls}${gap ? ' tc-gap' : ''}${clickable ? '' : ' tc-nointer'}${sel ? ' tc-sel' : ''}`}
                            title={dayTooltip(c.name, d)}
                            data-testid={`tc-cell-${c.employeeId}-${d.date}`}
                            onClick={clickable ? () => openPanel(c.employeeId, c.name, d.date) : undefined}
                          >
                            {future ? '' : (sum > 0 ? fmtHours(sum) : (gap ? '!' : ''))}
                          </span>
                        </td>
                      );
                    }
                    // TC-S4 — filtered mode: single-type value, single
                    // colour, no "!" and no combination code.
                    const val = perTypeCellValue(d, hoursFilter);
                    const meta = HOURS_FILTER_META[hoursFilter];
                    const clickable = !future;
                    const sel = isSel(c.employeeId, d.date);
                    return (
                      <td key={d.date}>
                        <span
                          className={`tc-cell ${val > 0 ? meta.cellClass : ''}${clickable ? '' : ' tc-nointer'}${sel ? ' tc-sel' : ''}`}
                          title={perTypeTooltip(c.name, d, hoursFilter)}
                          data-testid={`tc-cell-${c.employeeId}-${d.date}`}
                          onClick={clickable ? () => openPanel(c.employeeId, c.name, d.date) : undefined}
                        >
                          {future ? '' : (val > 0 ? fmtHours(val) : '·')}
                        </span>
                      </td>
                    );
                  })}
                  <td className="tc-n"><b>{fmtHours(total)}</b></td>
                </tr>
              );
            })}
            <tr data-testid="tc-day-totals">
              <td className="tc-name">Team total</td>
              {dayTotals.map((dt) => {
                const d = parseIso(dt.date);
                if (isWeekend(d)) return <td key={dt.date} className="tc-we" />;
                let sum;
                if (hoursFilter === 'all') {
                  sum = (Number(dt.billableHours) || 0) + (Number(dt.nonBillableHours) || 0) + (Number(dt.leaveHours) || 0);
                } else if (hoursFilter === 'sl') {
                  sum = teamShortLeaveByDate.get(dt.date) || 0;
                } else {
                  sum = Number(dt[HOURS_FILTER_META[hoursFilter].tag]) || 0;
                }
                return (
                  <td key={dt.date}>
                    <span className="tc-cell tc-nointer" data-testid={`tc-total-${dt.date}`}>
                      <b>{sum > 0 ? fmtHours(sum) : '·'}</b>
                    </span>
                  </td>
                );
              })}
              <td className="tc-n">
                <b>{fmtHours(hoursFilter === 'all'
                  ? dayTotals.reduce((a, dt) => a + (Number(dt.billableHours) || 0) + (Number(dt.nonBillableHours) || 0) + (Number(dt.leaveHours) || 0), 0)
                  : hoursFilter === 'sl'
                    ? Array.from(teamShortLeaveByDate.values()).reduce((a, v) => a + v, 0)
                    : dayTotals.reduce((a, dt) => a + (Number(dt[HOURS_FILTER_META[hoursFilter].tag]) || 0), 0))}</b>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  };

  // ─── Week view ───────────────────────────────────────────────────────
  const renderWeek = () => {
    const dayCols = (data?.consultants?.[0]?.days) || dayTotals;
    return (
      <table className="tc-g" data-testid="tc-table">
        <colgroup>
          <col style={{ width: 200 }} />
          {dayCols.map((_, i) => (
            <React.Fragment key={`cg-${i}`}>
              <col /><col /><col /><col />
            </React.Fragment>
          ))}
          <col style={{ width: 70 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="tc-l" rowSpan={2} style={{ position: 'relative' }} ref={filterAnchorRef}>
              Consultant
              <span
                className={`tc-fun${consFilter.length > 0 ? ' act' : ''}`}
                data-testid="tc-cons-funnel"
                onClick={(e) => { e.stopPropagation(); setFilterOpen((v) => !v); }}
              >▼</span>
              {filterOpen && (
                <ConsultantFunnel
                  allNames={allNames}
                  selected={consFilter}
                  onChange={setConsFilter}
                  onClose={() => setFilterOpen(false)}
                  active={consFilter.length > 0}
                />
              )}
            </th>
            {dayCols.map((d) => {
              const dObj = parseIso(d.date);
              return (
                <th key={`h-${d.date}`} colSpan={4} data-testid={`tc-th-${d.date}`}>
                  {DOW_ABBR[dObj.getDay()]} {fmtDdMm(dObj)}
                </th>
              );
            })}
            <th rowSpan={2}>Σ week</th>
          </tr>
          <tr>
            {dayCols.map((d) => (
              <React.Fragment key={`sub-${d.date}`}>
                <th title="Billable">B</th>
                <th title="Non-billable">NB</th>
                <th title="Leave / sick">L</th>
                <th>Σ</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredConsultants.map((c) => {
            let weekSum = 0;
            return (
              <tr key={c.employeeId} data-testid={`tc-row-${c.employeeId}`}>
                <td className="tc-name" title={c.name}>{c.name}</td>
                {c.days.map((d) => {
                  const b = Number(d.billableHours) || 0;
                  const n = Number(d.nonBillableHours) || 0;
                  const l = Number(d.leaveHours) || 0;
                  const s = b + n + l;
                  weekSum += s;
                  const future = isFuture(d.date);
                  const gap = !future && s === 0 && d.type !== 'Weekend' && d.type !== 'Holiday' && d.type !== 'Left';   // CEV-S3
                  const sel = isSel(c.employeeId, d.date);
                  return (
                    <React.Fragment key={d.date}>
                      <ColCell value={b} columnClass="tc-b"
                        isoDate={d.date} employeeId={c.employeeId} consultantName={c.name}
                        testid={`tc-cell-b-${c.employeeId}-${d.date}`} />
                      <ColCell value={n} columnClass="tc-nb"
                        isoDate={d.date} employeeId={c.employeeId} consultantName={c.name}
                        testid={`tc-cell-nb-${c.employeeId}-${d.date}`} />
                      <ColCell value={l} columnClass="tc-lv"
                        isoDate={d.date} employeeId={c.employeeId} consultantName={c.name}
                        testid={`tc-cell-l-${c.employeeId}-${d.date}`} />
                      <td>
                        <span
                          className={`tc-cell${gap ? ' tc-gap' : ''}${sel ? ' tc-sel' : ''}`}
                          data-testid={`tc-cell-sum-${c.employeeId}-${d.date}`}
                          onClick={() => openPanel(c.employeeId, c.name, d.date)}
                        >
                          <b>{future ? '' : (s > 0 ? fmtHours(s) : (gap ? '!' : '·'))}</b>
                        </span>
                      </td>
                    </React.Fragment>
                  );
                })}
                <td className="tc-n"><b>{fmtHours(weekSum)}</b></td>
              </tr>
            );
          })}
          <tr className="tc-sum" data-testid="tc-day-totals">
            <td className="tc-name">Team total</td>
            {dayTotals.map((dt) => {
              const b = Number(dt.billableHours) || 0;
              const n = Number(dt.nonBillableHours) || 0;
              const l = Number(dt.leaveHours) || 0;
              return (
                <React.Fragment key={dt.date}>
                  <td className="tc-n" style={{ textAlign: 'center' }}>{b > 0 ? fmtHours(b) : '·'}</td>
                  <td className="tc-n" style={{ textAlign: 'center' }}>{n > 0 ? fmtHours(n) : '·'}</td>
                  <td className="tc-n" style={{ textAlign: 'center' }}>{l > 0 ? fmtHours(l) : '·'}</td>
                  <td className="tc-n" style={{ textAlign: 'center' }}><b>{fmtHours(b + n + l)}</b></td>
                </React.Fragment>
              );
            })}
            <td className="tc-n">
              <b>{fmtHours(dayTotals.reduce((a, dt) => a + (Number(dt.billableHours) || 0) + (Number(dt.nonBillableHours) || 0) + (Number(dt.leaveHours) || 0), 0))}</b>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // ─── Day view ────────────────────────────────────────────────────────
  const renderDay = () => {
    const iso = fmtIso(range.from);
    const future = isFuture(iso);
    const weekend = isWeekend(range.from);
    const totals = { b: 0, n: 0, l: 0 };
    return (
      <table className="tc-g" data-testid="tc-table">
        <colgroup>
          <col style={{ width: 240 }} />
          <col /><col /><col /><col />
        </colgroup>
        <thead>
          <tr>
            <th className="tc-l" style={{ position: 'relative' }} ref={filterAnchorRef}>
              Consultant
              <span
                className={`tc-fun${consFilter.length > 0 ? ' act' : ''}`}
                data-testid="tc-cons-funnel"
                onClick={(e) => { e.stopPropagation(); setFilterOpen((v) => !v); }}
              >▼</span>
              {filterOpen && (
                <ConsultantFunnel
                  allNames={allNames}
                  selected={consFilter}
                  onChange={setConsFilter}
                  onClose={() => setFilterOpen(false)}
                  active={consFilter.length > 0}
                />
              )}
            </th>
            <th>Billable</th>
            <th>Non-billable</th>
            <th>Leave / sick</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {filteredConsultants.map((c) => {
            const d = c.days[0] || { billableHours: 0, nonBillableHours: 0, leaveHours: 0, type: 'Missing', date: iso };
            const b = Number(d.billableHours) || 0;
            const n = Number(d.nonBillableHours) || 0;
            const l = Number(d.leaveHours) || 0;
            const s = b + n + l;
            totals.b += b; totals.n += n; totals.l += l;
            const gap = !future && !weekend && s === 0 && d.type !== 'Holiday' && d.type !== 'Left';   // CEV-S3
            const sel = isSel(c.employeeId, iso);
            return (
              <tr key={c.employeeId} data-testid={`tc-row-${c.employeeId}`}>
                <td className="tc-name" title={c.name}>{c.name}</td>
                <ColCell value={b} columnClass="tc-b" big
                  isoDate={iso} employeeId={c.employeeId} consultantName={c.name}
                  testid={`tc-cell-b-${c.employeeId}`} />
                <ColCell value={n} columnClass="tc-nb" big
                  isoDate={iso} employeeId={c.employeeId} consultantName={c.name}
                  testid={`tc-cell-nb-${c.employeeId}`} />
                <ColCell value={l} columnClass="tc-lv" big
                  isoDate={iso} employeeId={c.employeeId} consultantName={c.name}
                  testid={`tc-cell-l-${c.employeeId}`} />
                <td>
                  <span
                    className={`tc-cell tc-big${gap ? ' tc-gap' : ''}${sel ? ' tc-sel' : ''}`}
                    data-testid={`tc-cell-sum-${c.employeeId}`}
                    onClick={() => openPanel(c.employeeId, c.name, iso)}
                  >
                    <b>{weekend || future ? '·' : (s > 0 ? fmtHours(s) : (gap ? '!' : '·'))}</b>
                  </span>
                </td>
              </tr>
            );
          })}
          <tr className="tc-sum" data-testid="tc-day-totals">
            <td className="tc-name">Team total</td>
            <td className="tc-n" style={{ textAlign: 'center' }}>{fmtHours(totals.b)}</td>
            <td className="tc-n" style={{ textAlign: 'center' }}>{fmtHours(totals.n)}</td>
            <td className="tc-n" style={{ textAlign: 'center' }}>{fmtHours(totals.l)}</td>
            <td className="tc-n" style={{ textAlign: 'center' }}><b>{fmtHours(totals.b + totals.n + totals.l)}</b></td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div className="reports-page tc-page" data-testid="team-calendar-overview" ref={scrollHostRef}>
      <div className="breadcrumb">Reports · Team Calendar Overview</div>

      <div className="tc-panel">
        <div className="tc-ph">
          <div className="tc-tt">Team — hours map</div>
          <div className="tc-ts">Month · Week · Day (click a cell to open the consultant panel)</div>
          <div className="tc-sp" />
          <div className="tc-ctl">
            <div className="tc-seg">
              {/* TC-S3.1 — switching views brings a genuinely different
                  table (month → week → day), so we clear the previous
                  payload and show the full Loading placeholder. */}
              <button type="button" className={view === 'm' ? 'on' : ''}
                      data-testid="tc-view-m"
                      onClick={() => { if (view !== 'm') { setData(null); setView('m'); } }}>Month</button>
              <button type="button" className={view === 'w' ? 'on' : ''}
                      data-testid="tc-view-w"
                      onClick={() => { if (view !== 'w') { setData(null); setView('w'); } }}>Week</button>
              <button type="button" className={view === 'd' ? 'on' : ''}
                      data-testid="tc-view-d"
                      onClick={() => { if (view !== 'd') { setData(null); setView('d'); } }}>Day</button>
            </div>
            <span className="tc-arr" data-testid="tc-prev"
                  onClick={() => setAnchor((a) => navStep(view, a, -1))}>◀</span>
            <span className="tc-plabel" data-testid="tc-period-label">{periodLabel(view, anchor)}</span>
            <span className="tc-arr" data-testid="tc-next"
                  onClick={() => setAnchor((a) => navStep(view, a, 1))}>▶</span>
          </div>
        </div>

        <div className="tc-pb">
          {error && <div className="tc-error" data-testid="tc-error">{error}</div>}
          {loading ? (
            <div className="tc-empty" data-testid="tc-loading">Loading…</div>
          ) : consultants.length === 0 ? (
            <div className="tc-empty">No consultants for this period.</div>
          ) : (
            <div
              className={`tc-scroll${refreshing ? ' tc-refreshing' : ''}`}
              data-testid="tc-scroll"
            >
              {view === 'm' && renderMonth()}
              {view === 'w' && renderWeek()}
              {view === 'd' && renderDay()}
            </div>
          )}

          {view === 'm' && hoursFilter !== 'all' ? (
            <div className="tc-legend" data-testid="tc-legend">
              Filter: {HOURS_FILTER_META[hoursFilter].label}
            </div>
          ) : (
            <div className="tc-legend" data-testid="tc-legend">
              Colour code (month):
              <i style={{ background: '#dbe9f9' }} />billable only
              <i style={{ background: '#e7ddf3' }} />combination
              <i style={{ background: '#fff3c2' }} />non-billable only
              <i style={{ background: '#e2e2e2' }} />paid/sick leave
              <span className="tc-gap-glyph">!</span> working day without logging
              <span style={{ marginLeft: 14, color: '#888' }}>· in week / day views the type is IN THE COLUMN</span>
            </div>
          )}
        </div>
      </div>

      {/* TC-S2 — spacer so the last consultant row can scroll clear of
          the docked panel at the bottom. Rendered only when the panel
          is open. */}
      {consPanel && (
        <div className="tc-dock-spacer" data-testid="tc-dock-spacer" />
      )}

      {consPanel && (
        <ConsultantTimesheetPanel
          key={consPanel.employeeId}
          employeeId={consPanel.employeeId}
          name={consPanel.name}
          initialDate={consPanel.date}
          onClose={() => setConsPanel(null)}
        />
      )}
    </div>
  );
}
